/**
 * API client per la gestione delle specialità veterinarie.
 *
 * Endpoints:
 * - GET /api/v1/admin/specialties/ - Lista specialità (auth required)
 * - GET /api/v1/admin/specialties/active - Solo specialità attive (no auth)
 * - POST /api/v1/admin/specialties/ - Crea specialità (admin)
 * - PUT /api/v1/admin/specialties/{key} - Aggiorna (admin)
 * - DELETE /api/v1/admin/specialties/{key} - Disattiva (admin)
 */

import api from './auth';
import axios from 'axios';

export interface Specialty {
  key: string;
  label: string;
  description: string;
  is_active: boolean;
}

export interface SpecialtyCreate {
  key: string;
  label: string;
  description: string;
}

const publicApi = axios.create({
  baseURL: '/api/v1',
});

export const specialtiesApi = {
  /**
   * Lista tutte le specialità (auth required).
   */
  list: async (): Promise<Specialty[]> => {
    const response = await api.get<Specialty[]>('/admin/specialties/');
    return response.data;
  },

  /**
   * Lista solo le specialità attive (no auth, per dropdown).
   */
  listActive: async (): Promise<Specialty[]> => {
    const response = await publicApi.get<Specialty[]>('/admin/specialties/active');
    return response.data;
  },

  /**
   * Crea una nuova specialità (admin).
   */
  create: async (data: SpecialtyCreate): Promise<Specialty> => {
    const response = await api.post<Specialty>('/admin/specialties/', data);
    return response.data;
  },

  /**
   * Aggiorna una specialità (admin).
   */
  update: async (key: string, data: { label?: string; description?: string; is_active?: boolean }): Promise<Specialty> => {
    const response = await api.put<Specialty>(`/admin/specialties/${key}`, data);
    return response.data;
  },

  /**
   * Disattiva una specialità (admin).
   */
  deactivate: async (key: string): Promise<void> => {
    await api.delete(`/admin/specialties/${key}`);
  },

  /**
   * Riattiva una specialità disattivata (admin).
   */
  activate: async (key: string): Promise<void> => {
    await api.post(`/admin/specialties/${key}/activate`);
  },
};
